import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

/**
 * Página de detalhes do plano comercial
 *
 * Integração com o contexto de CompanyUser:
 * - Usa useCompanyUser() para obter usuários do tipo USER (empresa)
 * - O backend já filtra automaticamente apenas usuários com TypeUser.USER
 * - O modal de adicionar usuários mostra apenas usuários de empresa disponíveis
 */
import {
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Paper,
  Typography,
  useTheme,
  Chip,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Divider,
  Modal,
  Skeleton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Star as StarIcon,
  AttachMoney as MoneyIcon,
  Rule as RuleIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Add as AddIcon,
  PersonAdd as UserPlusIcon,
  PersonRemove as UserMinusIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { AppBar } from 'components';
import {
  useAuth,
  useCommercialPlan,
  useExternalFont,
  useCompanyUser,
} from 'contexts';
import { TypeUser, ConsumptionFactorType } from 'types/enums';
import {
  ICommercialPlanViewModel,
  IUserViewModel,
  IFontRuleViewModel,
  ICollaboratorUserViewModel,
} from 'viewModels';
import { formatCurrency } from 'utils/formatCurrency';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import EditPlanModal from './EditPlanModal';
import DeletePlanModal from './DeletePlanModal';
import EditFontRuleModal from './EditFontRuleModal';
import {
  ModalWrapper,
  ModalHeader,
  ModalTitleContainer,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  CloseButton,
  LoadingContainer,
  LoadingText,
} from './modalStyles';
import { X } from 'lucide-react';

const CommercialPlanDetailsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { planId } = useParams<{ planId: string }>();
  const { user } = useAuth();
  const {
    commercialPlans,
    isLoading,
    handleFetch,
    handleActivate,
    handleDeactivate,
    handleSetAsDefault,
    handleGetPlanUsers,
    handleGetPlanFontRules,
    handleAddUserToPlan,
    handleRemoveUserFromPlan,
  } = useCommercialPlan();

  const { externalFonts } = useExternalFont();

  // Usando o contexto de CompanyUser que já filtra automaticamente usuários do tipo USER (empresa)
  const {
    companyUsers,
    handleFetch: fetchCompanyUsers,
    handleFetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCompanyUser();

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditFontRuleModal, setShowEditFontRuleModal] = useState(false);
  const [showAddUsersModal, setShowAddUsersModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState<IFontRuleViewModel | null>(
    null
  );

  // Estados para usuários
  const [users, setUsers] = useState<IUserViewModel[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Estados para usuários disponíveis
  const [availableUsers, setAvailableUsers] = useState<IUserViewModel[]>([]);
  const [availableUsersLoading, setAvailableUsersLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [availableUsersSearchTerm, setAvailableUsersSearchTerm] = useState('');

  // Estados para regras de fontes
  const [fontRules, setFontRules] = useState<IFontRuleViewModel[]>([]);
  const [fontRulesLoading, setFontRulesLoading] = useState(false);
  const [fontRulesError, setFontRulesError] = useState<string | null>(null);

  // Estado para mensagens de erro
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Verificar se o usuário é admin
  const isAdmin = user?.typeUser === TypeUser.ADM;

  // Encontrar o plano específico
  const plan = commercialPlans.find(p => p.id === planId);

  useEffect(() => {
    if (isAdmin && !commercialPlans.length) {
      handleFetch();
    }
  }, [isAdmin, commercialPlans.length, handleFetch]);

  // Carregar usuários do contexto quando o componente for montado
  useEffect(() => {
    if (isAdmin && (!companyUsers || companyUsers.length === 0)) {
      fetchCompanyUsers();
    }
  }, [isAdmin, companyUsers, fetchCompanyUsers]);

  useEffect(() => {
    if (plan) {
      loadUsers();
      loadFontRules();
    }
  }, [plan]);

  // Atualizar usuários disponíveis quando o contexto de companyUsers mudar
  useEffect(() => {
    if (companyUsers) {
      const currentUserIds = users.map(u => u.id);
      const available = companyUsers.filter(
        u => !currentUserIds.includes(u.id) && !u.commercialPlan?.id
      );
      setAvailableUsers(available);
    }
  }, [companyUsers, users]);

  const loadUsers = async () => {
    if (!plan) return;

    setUsersLoading(true);
    setUsersError(null);

    try {
      const planUsers = await handleGetPlanUsers(plan.id);
      if (planUsers) {
        setUsers(planUsers);
      }
    } catch (err) {
      setUsersError('Erro ao carregar usuários do plano');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadAvailableUsers = async () => {
    setAvailableUsersLoading(true);

    try {
      // Buscar usuários do contexto CompanyUser (já filtrados por tipo USER/empresa)
      await fetchCompanyUsers();

      if (companyUsers) {
        // Filtrar usuários que não estão no plano atual
        const currentUserIds = users.map(u => u.id);
        const available = companyUsers.filter(
          u => !currentUserIds.includes(u.id)
        );
        setAvailableUsers(available);
      }
    } catch (err) {
      console.error('Erro ao carregar usuários disponíveis:', err);
    } finally {
      setAvailableUsersLoading(false);
    }
  };

  const loadFontRules = async () => {
    if (!plan) return;

    setFontRulesLoading(true);
    setFontRulesError(null);

    try {
      const rules = await handleGetPlanFontRules(plan.id);
      if (rules) {
        setFontRules(rules);
      }
    } catch (err) {
      setFontRulesError('Erro ao carregar regras de fontes do plano');
    } finally {
      setFontRulesLoading(false);
    }
  };

  const handleAddUsers = async () => {
    if (!plan || selectedUsers.length === 0) return;

    setUsersLoading(true);
    setUsersError(null);

    try {
      for (const userId of selectedUsers) {
        await handleAddUserToPlan(plan.id, userId);
      }
      await loadUsers();
      setShowAddUsersModal(false);
      setSelectedUsers([]);
    } catch (err) {
      setUsersError('Erro ao adicionar usuários ao plano');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!plan) return;

    setUsersLoading(true);
    setUsersError(null);

    try {
      const success = await handleRemoveUserFromPlan(plan.id, userId);
      if (success) {
        await loadUsers();
      } else {
        setUsersError('Erro ao remover usuário do plano');
      }
    } catch (err) {
      setUsersError('Erro ao remover usuário do plano');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleOpenAddUsersModal = () => {
    setShowAddUsersModal(true);
    // Carregar usuários do contexto se ainda não foram carregados
    if (!companyUsers || companyUsers.length === 0) {
      fetchCompanyUsers();
    }
  };

  const handleCloseAddUsersModal = () => {
    setShowAddUsersModal(false);
    setSelectedUsers([]);
    setAvailableUsersSearchTerm('');
  };

  const handleUserSelection = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleMenuAction: MenuProps['onClick'] = ({ key }) => {
    if (plan) {
      // Limpar mensagem de erro anterior
      setErrorMessage(null);

      if (key === 'edit') {
        setShowEditModal(true);
      } else if (key === 'toggle') {
        // Impedir desativação do plano padrão
        if (plan.isDefault) {
          setErrorMessage('Não é possível desativar o plano padrão. Defina outro plano como padrão primeiro.');
          return;
        }

        if (plan.isActive) {
          handleDeactivate(plan.id);
        } else {
          handleActivate(plan.id);
        }
      } else if (key === 'default') {
        handleSetAsDefault(plan.id);
      } else if (key === 'delete') {
        // Impedir exclusão do plano padrão
        if (plan.isDefault) {
          setErrorMessage('Não é possível excluir o plano padrão. Defina outro plano como padrão primeiro.');
          return;
        }

        setShowDeleteModal(true);
      }
    }
  };

  const getMenuItems = (plan: ICommercialPlanViewModel): MenuProps['items'] => [
    ...(plan.isDefault
      ? [
        {
          key: 'info',
          label: 'Plano Padrão - Ações Restritas',
          icon: <InfoIcon sx={{ fontSize: 18 }} />,
          disabled: true,
          style: {
            color: '#6b7280',
            fontStyle: 'italic',
            cursor: 'default',
          },
        },
      ]
      : []),
    {
      key: 'toggle',
      label: plan.isActive ? 'Desativar plano' : 'Ativar plano',
      icon: plan.isActive ? (
        <VisibilityOffIcon sx={{ fontSize: 18 }} />
      ) : (
        <VisibilityIcon sx={{ fontSize: 18 }} />
      ),
      style: {
        color: plan.isActive ? '#faad14' : '#6df178',
        opacity: plan.isDefault ? 0.5 : 1,
      },
      disabled: plan.isDefault,
      title: plan.isDefault ? 'Não é possível desativar o plano padrão. Defina outro plano como padrão primeiro.' : undefined,
    },
    ...(plan.isDefault
      ? []
      : [
        {
          key: 'default',
          label: 'Definir como padrão',
          icon: <StarIcon sx={{ fontSize: 18 }} />,
        },
      ]),
  ];

  const getFactorTypeLabel = (type: ConsumptionFactorType) => {
    switch (type) {
      case ConsumptionFactorType.PER_EXECUTION:
        return 'Por Execução';
      case ConsumptionFactorType.PER_ITEM:
        return 'Por Item';
      case ConsumptionFactorType.PER_TIME:
        return 'Por Tempo';
      default:
        return 'Desconhecido';
    }
  };

  const getFactorTypeColor = (type: ConsumptionFactorType) => {
    switch (type) {
      case ConsumptionFactorType.PER_EXECUTION:
        return 'success';
      case ConsumptionFactorType.PER_ITEM:
        return 'warning';
      case ConsumptionFactorType.PER_TIME:
        return 'info';
      default:
        return 'default';
    }
  };

  // Função para obter o nome da fonte baseado na chave
  const getFontName = (fontKey: string) => {
    const font = externalFonts.find(f => f.key === fontKey);
    return font ? font.name : fontKey;
  };

  // Componente de skeleton para a tabela de usuários
  const UsersTableSkeleton = () => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Skeleton variant="rectangular" width={20} height={20} />
            </TableCell>
            <TableCell>Usuário</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(6)].map((_, index) => (
            <TableRow key={index}>
              <TableCell padding="checkbox">
                <Skeleton variant="rectangular" width={20} height={20} />
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" gap={2}>
                  <Skeleton variant="circular" width={32} height={32} />
                  <Skeleton variant="text" width={120} height={20} />
                </Box>
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={180} height={20} />
              </TableCell>
              <TableCell>
                <Skeleton
                  variant="rectangular"
                  width={60}
                  height={24}
                  sx={{ borderRadius: 1 }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Componente de skeleton para a lista de usuários do plano
  const UsersListSkeleton = () => (
    <List dense>
      {[...Array(4)].map((_, index) => (
        <ListItem
          key={index}
          sx={{
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 1,
            mb: 1,
            '&:last-child': { mb: 0 },
          }}
        >
          <ListItemIcon>
            <Skeleton variant="circular" width={32} height={32} />
          </ListItemIcon>
          <ListItemText
            primary={<Skeleton variant="text" width={120} height={20} />}
            secondary={
              <Box>
                <Skeleton
                  variant="text"
                  width={150}
                  height={16}
                  sx={{ mb: 0.5 }}
                />
                <Skeleton
                  variant="rectangular"
                  width={50}
                  height={20}
                  sx={{ borderRadius: 1 }}
                />
              </Box>
            }
          />
          <Skeleton variant="circular" width={24} height={24} />
        </ListItem>
      ))}
    </List>
  );

  // Componente de skeleton para a tabela de regras de fontes
  const FontRulesTableSkeleton = () => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Fonte</TableCell>
            <TableCell>Tipo de Fator</TableCell>
            <TableCell>Valor</TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(3)].map((_, index) => (
            <TableRow key={index}>
              <TableCell>
                <Skeleton
                  variant="text"
                  width={100}
                  height={20}
                  sx={{ mb: 0.5 }}
                />
                <Skeleton variant="text" width={150} height={16} />
              </TableCell>
              <TableCell>
                <Skeleton
                  variant="rectangular"
                  width={80}
                  height={24}
                  sx={{ borderRadius: 1 }}
                />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={80} height={20} />
              </TableCell>
              <TableCell align="right">
                <Box display="flex" gap={1} justifyContent="flex-end">
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="circular" width={24} height={24} />
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const filteredUsers = users.filter(
    user =>
      user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  const filteredAvailableUsers = availableUsers.filter(
    user =>
      // Garantir que apenas usuários do tipo USER (empresa) sejam exibidos
      user.typeUser === 0 &&
      (user.name
        .toLowerCase()
        .includes(availableUsersSearchTerm.toLowerCase()) ||
        user.email
          .toLowerCase()
          .includes(availableUsersSearchTerm.toLowerCase()))
  );

  if (!isAdmin) {
    return (
      <>
        <AppBar />
        <Container maxWidth="lg" sx={{ mt: 10, mb: 4 }}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="error">
              Acesso negado. Apenas administradores podem acessar esta página.
            </Typography>
          </Paper>
        </Container>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <AppBar />
        <Container sx={{ minWidth: '90vw', minHeight: '100vh', pt: 10, pb: 4 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: '400px' }}
          >
            <CircularProgress size={40} />
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ ml: 2, fontStyle: 'italic' }}
            >
              Carregando detalhes do plano...
            </Typography>
          </Box>
        </Container>
      </>
    );
  }

  if (!plan) {
    return (
      <>
        <AppBar />
        <Container sx={{ minWidth: '90vw', minHeight: '100vh', pt: 10, pb: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              Plano não encontrado
            </Alert>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              O plano solicitado não foi encontrado ou não existe.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/commercialPlans')}
            >
              Voltar para Planos
            </Button>
          </Paper>
        </Container>
      </>
    );
  }

  return (
    <>
      <AppBar />
      <Container sx={{ minWidth: '90vw', minHeight: '100vh', pt: 10, pb: 4 }}>
        {/* Header com botão voltar */}
        <Box display="flex" alignItems="center" sx={{ mb: 3 }}>
          <IconButton
            onClick={() => navigate('/commercialPlans')}
            sx={{ color: theme.palette.primary.main, mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
            Detalhes do Plano
          </Typography>
        </Box>

        {/* Mensagem de erro */}
        {errorMessage && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setErrorMessage(null)}
          >
            {errorMessage}
          </Alert>
        )}

        {/* Layout principal com duas colunas */}
        <Grid container spacing={3}>
          {/* Coluna esquerda - Cards de informações e regras */}
          <Grid item xs={12} md={8}>
            {/* Card principal com informações gerais */}
            <Card
              sx={{
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                position: 'relative',
                mb: 3,
              }}
            >
              <CardContent sx={{ p: 4 }}>
                {/* Header do card com título e botões */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  sx={{ mb: 4 }}
                >
                  <Box flex={1}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={2}
                      sx={{ mb: 1 }}
                    >
                      <Typography
                        variant="h4"
                        color="primary"
                        sx={{ fontWeight: 600 }}
                      >
                        {plan.name}
                      </Typography>
                      <Box display="flex" gap={1}>
                        <Chip
                          label={plan.isActive ? 'Ativo' : 'Inativo'}
                          size="small"
                          color={plan.isActive ? 'success' : 'error'}
                          variant="outlined"
                          sx={{ fontSize: '0.75rem', height: 20 }}
                        />
                        {plan.isDefault && (
                          <Chip
                            label="Padrão"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: '0.75rem', height: 20 }}
                          />
                        )}
                      </Box>
                    </Box>
                    <Tooltip title={plan.description || 'Sem descrição'}>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                          mb: 1,
                          textAlign: 'left',
                          lineHeight: 1.4,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '800px',
                        }}
                      >
                        {plan.description || 'Sem descrição'}
                      </Typography>
                    </Tooltip>
                  </Box>

                  {/* Botões de ação */}
                  <Box display="flex" gap={1} sx={{ ml: 2 }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setShowEditModal(true)}
                    >
                      Editar
                    </Button>
                    <Dropdown
                      menu={{
                        items: getMenuItems(plan),
                        onClick: handleMenuAction,
                      }}
                      trigger={['click']}
                      placement="bottomRight"
                      arrow
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SettingsIcon />}
                      >
                        Ações
                      </Button>
                    </Dropdown>
                  </Box>
                </Box>

                {/* Informações principais em grid */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={3}>
                    <Box
                      textAlign="center"
                      sx={{
                        p: 2.5,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: 'grey.100',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      <MoneyIcon color="primary" sx={{ fontSize: 28, mb: 1 }} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        Consumo Mínimo
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {formatCurrency(plan.minimumConsumption)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box
                      textAlign="center"
                      sx={{
                        p: 2.5,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: 'grey.100',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      <SecurityIcon
                        color="primary"
                        sx={{ fontSize: 28, mb: 1 }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        Máx. Fontes
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {plan.maxPublicFonts}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box
                      textAlign="center"
                      sx={{
                        p: 2.5,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: 'grey.100',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      <TrendingUpIcon
                        color="primary"
                        sx={{ fontSize: 28, mb: 1 }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        Taxa Extra
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {plan.extraFontsTaxRate}%
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box
                      textAlign="center"
                      sx={{
                        p: 2.5,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: 'grey.100',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      <PeopleIcon
                        color="primary"
                        sx={{ fontSize: 28, mb: 1 }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        Usuários
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {plan.userCount}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Informações adicionais integradas */}
                <Box
                  sx={{
                    mt: 3,
                    pt: 3,
                    borderTop: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <RuleIcon color="primary" sx={{ fontSize: 20 }} />
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            Regras de Fontes
                          </Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {plan.fontRuleCount} regra
                            {plan.fontRuleCount !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <CalendarIcon color="primary" sx={{ fontSize: 20 }} />
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            Criado em
                          </Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {new Date(plan.createdAt).toLocaleDateString(
                              'pt-BR'
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <CalendarIcon color="primary" sx={{ fontSize: 20 }} />
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            Última Atualização
                          </Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {new Date(
                              plan.updatedAt || plan.createdAt
                            ).toLocaleDateString('pt-BR')}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>

            {/* Card com regras de fontes */}
            <Card
              sx={{
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Regras de Fontes
                  </Typography>
                </Box>

                {fontRulesError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {fontRulesError}
                  </Alert>
                )}

                {fontRulesLoading ? (
                  <FontRulesTableSkeleton />
                ) : fontRules.length === 0 ? (
                  <Box textAlign="center" py={4}>
                    <RuleIcon
                      sx={{
                        fontSize: 48,
                        color: 'text.secondary',
                        mb: 2,
                      }}
                    />
                    <Typography variant="h6" color="text.secondary" mb={1}>
                      Nenhuma regra de fonte encontrada
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Este plano ainda não possui regras de fontes configuradas.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Fonte</TableCell>
                          <TableCell>Tipo de Fator</TableCell>
                          <TableCell>Valor</TableCell>
                          <TableCell align="right">Editar</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fontRules.map(rule => (
                          <TableRow key={rule.fontKey}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {getFontName(rule.fontKey)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: '200px',
                                  textAlign: 'left',
                                }}
                              >
                                {rule.fontKey} •{' '}
                                {rule.description || 'Sem descrição'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getFactorTypeLabel(rule.factorType)}
                                size="small"
                                color={
                                  getFactorTypeColor(rule.factorType) as any
                                }
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatCurrency(rule.value)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Box
                                display="flex"
                                gap={1}
                                justifyContent="flex-end"
                              >
                                <Tooltip title="Editar regra">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedRule(rule);
                                      setShowEditFontRuleModal(true);
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Coluna direita - Card dos usuários */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CardContent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  overflow: 'hidden',
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Usuários do Plano
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<UserPlusIcon />}
                    onClick={handleOpenAddUsersModal}
                  >
                    Adicionar
                  </Button>
                </Box>

                {usersError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {usersError}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  placeholder="Buscar usuários..."
                  value={userSearchTerm}
                  onChange={e => setUserSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  sx={{ mb: 2 }}
                />

                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    minHeight: '200px',
                  }}
                >
                  {usersLoading ? (
                    <UsersListSkeleton />
                  ) : filteredUsers.length === 0 ? (
                    <Box textAlign="center" py={4}>
                      <PersonIcon
                        sx={{
                          fontSize: 48,
                          color: 'text.secondary',
                          mb: 2,
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {userSearchTerm
                          ? 'Nenhum usuário corresponde à busca.'
                          : 'Nenhum usuário associado.'}
                      </Typography>
                    </Box>
                  ) : (
                    <List dense>
                      {filteredUsers.map(user => (
                        <ListItem
                          key={user.id}
                          sx={{
                            border: '1px solid',
                            borderColor: 'grey.200',
                            borderRadius: 1,
                            mb: 1,
                            '&:last-child': { mb: 0 },
                          }}
                        >
                          <ListItemIcon>
                            <Avatar sx={{ width: 32, height: 32 }}>
                              {user.name.charAt(0).toUpperCase()}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight={500}>
                                {user.name}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {user.email}
                                </Typography>
                                <Box mt={0.5}>
                                  <Chip
                                    label={!user.disabled ? 'Ativo' : 'Inativo'}
                                    size="small"
                                    color={!user.disabled ? 'success' : 'error'}
                                    variant="outlined"
                                  />
                                </Box>
                              </Box>
                            }
                          />
                          <Tooltip title="Remover do plano">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveUser(user.id)}
                              disabled={usersLoading}
                            >
                              <UserMinusIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Modal para Adicionar Usuários */}
        <Modal
          open={showAddUsersModal}
          onClose={handleCloseAddUsersModal}
          disableScrollLock={false}
          keepMounted={false}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          <ModalWrapper>
            <ModalHeader>
              <ModalTitleContainer>
                <ModalTitle id="modal-title">
                  Adicionar Usuários ao Plano
                </ModalTitle>
                <ModalDescription id="modal-description">
                  Selecione os usuários do tipo "Empresa" que deseja adicionar
                  ao plano "{plan?.name}"
                </ModalDescription>
              </ModalTitleContainer>
              <CloseButton onClick={handleCloseAddUsersModal}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <ModalContent>
              <TextField
                fullWidth
                placeholder="Buscar usuários disponíveis..."
                value={availableUsersSearchTerm}
                onChange={e => setAvailableUsersSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{ mb: 3 }}
              />

              {availableUsersLoading ? (
                <UsersTableSkeleton />
              ) : filteredAvailableUsers.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <PersonIcon
                    sx={{
                      fontSize: 48,
                      color: 'text.secondary',
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Nenhum usuário disponível
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {availableUsersSearchTerm
                      ? 'Nenhum usuário do tipo "Empresa" corresponde à busca.'
                      : 'Todos os usuários do tipo "Empresa" já estão associados a este plano.'}
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox"></TableCell>
                          <TableCell>Usuário</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredAvailableUsers.map(user => (
                          <TableRow key={user.id}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedUsers.includes(user.id)}
                                onChange={() => handleUserSelection(user.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={2}>
                                <Avatar sx={{ width: 32, height: 32 }}>
                                  {user.name.charAt(0).toUpperCase()}
                                </Avatar>
                                <Typography variant="body2" fontWeight={500}>
                                  {user.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {user.email}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  user.typeUser === 0
                                    ? 'Empresa'
                                    : user.typeUser === 1
                                      ? 'Colaborador'
                                      : 'Administrador'
                                }
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={!user.disabled ? 'Ativo' : 'Inativo'}
                                size="small"
                                color={!user.disabled ? 'success' : 'error'}
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Informações e Botão Carregar Mais */}
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    mt={2}
                    mb={1}
                    gap={1}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      textAlign="center"
                    >
                      {companyUsers?.length || 0} usuários carregados
                      {hasNextPage && ' (mais disponíveis)'}
                    </Typography>

                    {hasNextPage && (
                      <Button
                        onClick={() => {
                          handleFetchNextPage();
                        }}
                        disabled={isFetchingNextPage}
                        variant="outlined"
                        size="small"
                        startIcon={
                          isFetchingNextPage ? (
                            <CircularProgress size={16} />
                          ) : (
                            <AddIcon />
                          )
                        }
                      >
                        {isFetchingNextPage
                          ? 'Carregando...'
                          : 'Carregar Mais Usuários'}
                      </Button>
                    )}
                  </Box>
                </>
              )}
            </ModalContent>

            <ModalFooter>
              <button
                onClick={handleCloseAddUsersModal}
                disabled={usersLoading}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  background: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleAddUsers}
                disabled={selectedUsers.length === 0 || usersLoading}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  background: '#293778',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  opacity: selectedUsers.length === 0 || usersLoading ? 0.5 : 1,
                }}
              >
                {usersLoading ? (
                  <LoadingContainer>
                    <CircularProgress size={16} />
                    <LoadingText>Adicionando...</LoadingText>
                  </LoadingContainer>
                ) : (
                  `Adicionar ${selectedUsers.length > 0 ? `(${selectedUsers.length})` : ''
                  }`
                )}
              </button>
            </ModalFooter>
          </ModalWrapper>
        </Modal>

        {/* Modals */}
        <EditPlanModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          plan={plan}
          onSuccess={() => {
            setShowEditModal(false);
            handleFetch();
          }}
        />

        <DeletePlanModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          plan={plan}
          onSuccess={() => {
            setShowDeleteModal(false);
            navigate('/commercialPlans');
          }}
        />

        {selectedRule && (
          <EditFontRuleModal
            open={showEditFontRuleModal}
            onClose={() => {
              setShowEditFontRuleModal(false);
              setSelectedRule(null);
            }}
            planId={plan.id}
            rule={selectedRule}
            onSuccess={() => {
              setShowEditFontRuleModal(false);
              setSelectedRule(null);
              loadFontRules();
            }}
          />
        )}
      </Container>
    </>
  );
};

export default CommercialPlanDetailsPage;
